import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PlanBoundary } from '../plan/PlanBoundary';
import { RoadmapPage } from '../plan/routes/Roadmap';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useProjectReturn } from '../lib/projectReturn';

/**
 * The roadmap, and the slide deck it generates.
 *
 * A page of its own rather than a third board tab: it carries its own header and its own
 * zoom, it is read-only, and it is the thing somebody opens to send a picture to a client —
 * not one of the three ways of working a board. The Gantt's deck button lands here.
 *
 * Scoped by project id like every other Hub route. The port kit's own button pointed at
 * `/p/{projectKey}/roadmap`, which needed a code-to-id lookup nothing else in the app does;
 * the button now points here instead.
 */
export function RoadmapRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const { session } = useSession();
  const { backTo } = useProjectReturn(projectId);
  const [crumb, setCrumb] = useState<{ project: string; companyId: string } | null>(null);

  /**
   * Which company this project belongs to.
   *
   * Fetched rather than read from the session, which carries companies but no mapping from a
   * project to one. It is one small request and the crumb simply appears when it lands — the
   * roadmap itself does not wait for it.
   *
   * Only the ID comes back — `/projects/:id` carries `project.companyId` and no company name —
   * so the name is looked up in the session, which already has both. Checked rather than
   * assumed: the first version of this printed the word "Company".
   */
  useEffect(() => {
    if (!projectId) return;
    let live = true;
    void api
      .get<{ project: { name: string; companyId: string } }>(`/projects/${projectId}`)
      .then((data) => {
        if (!live) return;
        setCrumb({ project: data.project.name, companyId: data.project.companyId });
      })
      .catch(() => {
        // A crumb is a convenience. If it cannot be resolved the page still works.
      });
    return () => {
      live = false;
    };
  }, [projectId]);

  if (!projectId) return null;

  /**
   * The imported roadmap draws no header of its own, so there was no way back from it at all —
   * not to the project, not to the customer. The crumb lives here rather than in the kit's
   * component, which stays as it arrived.
   */
  return (
    <PlanBoundary>
      <div className="eyebrow crumbs" style={{ marginBottom: 10 }}>
        {/* First in the trail, because it is the door this person actually came through. */}
        {backTo && (
          <>
            <Link to={backTo} className="muted">
              Project management
            </Link>
            <span aria-hidden>›</span>
          </>
        )}
        {crumb && (
          <>
            <Link to={`/companies/${crumb.companyId}`} className="muted">
              {session?.companies.find((c) => c.id === crumb.companyId)?.name ?? 'Company'}
            </Link>
            <span aria-hidden>›</span>
          </>
        )}
        <Link to={`/projects/${projectId}`} className="muted">
          {crumb?.project ?? 'Project overview'}
        </Link>
      </div>
      <RoadmapPage scopeTo={projectId} />
    </PlanBoundary>
  );
}
